import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FilterIconSvg from "../../assets/image/filter.svg";
import { parseApiDate, toApiDate } from "../../utils/dateRange";

/** Inline calendar — same react-datepicker config as Dashboard */
export const SingleDatePanel = ({
  selected,
  onChange,
  minDate,
  maxDate,
  disabled = false,
}) => (
  <DatePicker
    selected={selected}
    onChange={onChange}
    inline
    showMonthDropdown
    showYearDropdown
    dropdownMode="select"
    minDate={minDate}
    maxDate={maxDate}
    disabled={disabled}
  />
);

const formatButtonLabel = (value) => {
  const d = parseApiDate(value);
  if (!d) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * Single-date picker (Dashboard-style calendar).
 *
 * variant="icon" — filter icon popover, closes on select (Dashboard)
 * variant="popover" — custom trigger + calendar (applies on select; optional Clear)
 * variant="field" — labeled button + popover (forms / filters)
 * variant="panel" — calendar only (embed in existing popover)
 */
const SingleDatePicker = ({
  variant = "icon",
  value = "",
  onChange,
  minDate,
  maxDate,
  disabled = false,
  label = "",
  placeholder = "Select date",
  align = "right",
  wrapperClassName = "",
  buttonClassName = "",
  className = "",
  closeOnSelect = true,
  onClear,
  filterIcon = FilterIconSvg,
}) => {
  const ref = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  const selectedDate = parseApiDate(value);
  const min = minDate ? (minDate instanceof Date ? minDate : parseApiDate(minDate)) : undefined;
  const max = maxDate ? (maxDate instanceof Date ? maxDate : parseApiDate(maxDate)) : undefined;

  useEffect(() => {
    if (variant === "panel") return undefined;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [variant]);

  const handleSelect = (date) => {
    if (!date) return;
    onChange?.(toApiDate(date));
    if (closeOnSelect) {
      setIsOpen(false);
    }
  };

  const handleClear = () => {
    onChange?.("");
    onClear?.();
    setIsOpen(false);
  };

  if (variant === "panel") {
    return (
      <SingleDatePanel
        selected={selectedDate}
        onChange={handleSelect}
        minDate={min}
        maxDate={max}
        disabled={disabled}
      />
    );
  }

  const popoverAlign =
    align === "left" ? "left-0" : "right-0";

  const calendarDropdown = (
    <div
      className={`absolute ${popoverAlign} mt-2 rounded-lg bg-white p-3 shadow-lg z-50 ${
        variant === "icon" ? "" : "border"
      }`}
    >
      <SingleDatePanel
        selected={selectedDate}
        onChange={handleSelect}
        minDate={min}
        maxDate={max}
        disabled={disabled}
      />
      {(value || onClear) && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <button
            type="button"
            className="w-full rounded border px-3 py-1.5 text-xs hover:bg-gray-50"
            onClick={handleClear}
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );

  if (variant === "icon") {
    const defaultBtn =
      "p-2 border rounded hover:bg-gray-100 bg-white inline-flex items-center justify-center";
    return (
      <div className={`relative ${wrapperClassName}`} ref={ref}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={buttonClassName || defaultBtn}
          disabled={disabled}
        >
          <img
            src={filterIcon}
            alt="Select date"
            className="w-4 h-4 invert grayscale brightness-100"
          />
        </button>
        {isOpen && calendarDropdown}
      </div>
    );
  }

  if (variant === "popover") {
    return (
      <div className={`relative ${wrapperClassName}`} ref={ref}>
        <button
          type="button"
          className={buttonClassName}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
        >
          <img
            src={filterIcon}
            alt="Date filter"
            className="w-4 h-4 invert grayscale brightness-100"
          />
        </button>
        {isOpen && (
          <div
            className={`absolute ${popoverAlign} top-[calc(100%+8px)] z-20 min-w-[16rem] rounded-lg border bg-white p-3 shadow-lg`}
          >
            {label ? (
              <label className="mb-1 block text-xs text-gray-500">{label}</label>
            ) : null}
            <SingleDatePanel
              selected={selectedDate}
              onChange={handleSelect}
              minDate={min}
              maxDate={max}
              disabled={disabled}
            />
            {value && (
              <div className="mt-3 border-t border-gray-100 pt-3">
                <button
                  type="button"
                  className="w-full rounded border px-3 py-1.5 text-xs hover:bg-gray-50"
                  onClick={handleClear}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const display = formatButtonLabel(value) || placeholder;

  return (
    <div className={`relative ${className}`} ref={ref}>
      {label ? (
        <label className="mb-1 block text-sm font-UbuntuMedium text-gray-700">{label}</label>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`flex h-[44px] w-full min-w-[10rem] items-center justify-between rounded-lg border bg-white px-3 text-sm outline-none focus:border-[#0B3E64] ${
          disabled ? "cursor-not-allowed bg-slate-100 opacity-70" : ""
        } border-[#0B3E64]/40`}
      >
        <span className={value ? "text-gray-800" : "text-gray-500"}>{display}</span>
        <img src={filterIcon} alt="" className="ml-2 h-4 w-4 shrink-0 invert grayscale brightness-100" />
      </button>
      {isOpen && !disabled && calendarDropdown}
    </div>
  );
};

export default SingleDatePicker;
